/**
 * Gestión de tema (Light / Dark / Sistema).
 *
 * La preferencia del usuario vive en el store de Zustand (persistida en
 * localStorage como parte de `finance-app-store`). Acá solo viven las funciones
 * puras que RESUELVEN esa preferencia contra el sistema operativo y la APLICAN
 * al <html> (clase `.light` / `.dark` + meta theme-color).
 *
 * El primer pintado lo maneja el script anti-FOUC en index.html (lee el mismo
 * localStorage antes de que monte React). Este módulo mantiene todo sincronizado
 * después, y reacciona a cambios del SO cuando el modo es "system".
 */

export type ThemeChoice = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

/** Color de la barra del navegador/PWA por tema (debe matchear --background). */
const THEME_COLOR: Record<ResolvedTheme, string> = {
  dark: "#1c1b19", // charcoal cálido
  light: "#faf8f4", // off-white cálido
};

const MEDIA_QUERY = "(prefers-color-scheme: dark)";

export function systemPrefersDark(): boolean {
  return typeof window !== "undefined" && window.matchMedia(MEDIA_QUERY).matches;
}

/** Convierte la preferencia del usuario en el tema concreto a pintar. */
export function resolveTheme(choice: ThemeChoice): ResolvedTheme {
  if (choice === "system") return systemPrefersDark() ? "dark" : "light";
  return choice;
}

/** Aplica el tema resuelto al documento. Idempotente. */
export function applyResolvedTheme(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  root.classList.remove("light", "dark");
  root.classList.add(resolved);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", THEME_COLOR[resolved]);
}

/**
 * Suscribe a los cambios de tema del SO. Solo tiene efecto real cuando el modo
 * elegido es "system". Devuelve una función de limpieza.
 */
export function watchSystemTheme(onChange: () => void): () => void {
  const mq = window.matchMedia(MEDIA_QUERY);
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
}
